import { Modal, Form, DatePicker, Input, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Audit } from "../../../types/audit";

interface CancelAuditModalProps {
  visible: boolean;
  onClose: () => void;
  audit: Audit | null;
  onSuccess?: () => void;
}

interface FormValues {
  cancellation_date: dayjs.Dayjs;
  cancellation_reason: string;
}

/**
 * Coordinator-only modal for cancelling an audit. Posts to
 * /audits/{id}/cancel/ which the backend validates is being called by
 * a user_type=4 user, sets cancelled_by/date/reason and flips
 * status_of_audit to 'canceled'.
 */
export const CancelAuditModal = ({
  visible,
  onClose,
  audit,
  onSuccess,
}: CancelAuditModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    if (!audit) return;
    setSubmitting(true);
    try {
      const { apiClient } = await import("../../../services/api/apiClient");
      await apiClient.post(`/audits/${audit.id}/cancel/`, {
        cancellation_date: values.cancellation_date.format("YYYY-MM-DD"),
        cancellation_reason: values.cancellation_reason.trim(),
      });
      message.success(
        t("audit.cancel_success", "Auditoría cancelada correctamente"),
      );
      form.resetFields();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        t("audit.cancel_error", "No se pudo cancelar la auditoría");
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("audit.cancel_title", "Cancelar auditoría")}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("audit.cancel_confirm", "Cancelar auditoría")}
      okButtonProps={{ danger: true, loading: submitting }}
      cancelText={t("common.cancel", "Cancelar")}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ cancellation_date: dayjs() }}
        autoComplete="off"
      >
        <Form.Item
          name="cancellation_date"
          label={t("audit.cancellation_date", "Fecha de cancelación")}
          rules={[
            {
              required: true,
              message: t(
                "audit.cancellation_date_required",
                "La fecha de cancelación es obligatoria",
              ),
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Form.Item>

        <Form.Item
          name="cancellation_reason"
          label={t("audit.cancellation_reason", "Motivo de cancelación")}
          rules={[
            {
              required: true,
              message: t(
                "audit.cancellation_reason_required",
                "El motivo es obligatorio",
              ),
            },
            {
              min: 5,
              message: t(
                "audit.cancellation_reason_min",
                "Describe el motivo (mínimo 5 caracteres)",
              ),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            maxLength={2000}
            showCount
            placeholder={t(
              "audit.cancellation_reason_placeholder",
              "Explica brevemente por qué se cancela la auditoría",
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
